/**
 * Camada de DADOS/orquestração da convergência (usa supabase). A regra pura vive em
 * `lib/convergencia.ts`. Aqui buscamos os fatos do CNPJ, a cobertura (RPC) e gravamos
 * o resultado no pedido + timeline. Tudo defensivo: erros não devem quebrar o fechamento.
 */
import { supabase } from '@/integrations/supabase/client';
import {
  avaliarConvergenciaGate,
  type FatosCnpj,
  type CoberturaCnpj,
  type ResultadoGate,
  type ConvergenciaPayload,
  type ClassificacaoConvergencia,
  type StatusConvergencia,
} from './convergencia';

const FAMILIAS_MOVEL = new Set(['Móvel', 'Totalização']);
const FAMILIAS_FIXA = new Set(['Fibra', 'Fixa Básica', 'Fixa Avançada']);

export interface AvaliacaoPedido {
  cnpj: string;
  fatos: FatosCnpj;
  cobertura: CoberturaCnpj;
  gate: ResultadoGate;
}

async function getCnpj(pedido: any): Promise<string | null> {
  if (pedido?.cnpj) return String(pedido.cnpj);
  if (pedido?.cliente_id) {
    const { data } = await supabase.from('clientes').select('cnpj').eq('id', pedido.cliente_id).maybeSingle();
    return data?.cnpj ? String(data.cnpj) : null;
  }
  return null;
}

export async function getCoberturaCnpj(cnpj: string): Promise<CoberturaCnpj> {
  // RPC ainda não consta no types.ts gerado → cast localizado.
  const { data } = await (supabase.rpc as any)('convergencia_cobertura_cnpj', { p_cnpj: cnpj });
  const row: any = Array.isArray(data) ? data[0] : data;
  return {
    temCobertura: row?.tem_cobertura ?? null,
    cep: row?.cep ?? null,
    territorioAace: row?.territorio_aace ?? null,
    territorioNosso: row?.territorio_nosso ?? null,
    qtdLinhasVoz: row?.qtd_linhas_voz ?? null,
  };
}

/** Cobertura de fibra para um CEP informado manualmente (fallback quando o CNPJ não tem CEP). */
export async function coberturaPorCep(cepRaw: string): Promise<boolean> {
  const cep = (cepRaw || '').replace(/\D/g, '');
  if (cep.length !== 8) return false;
  // Tabela ainda não consta no types.ts gerado → cast localizado.
  const { data } = await (supabase as any)
    .from('cep_cobertura_claro_bl')
    .select('ativo')
    .eq('cep', cep)
    .eq('ativo', true)
    .limit(1);
  return !!(data && data.length > 0);
}

/** temMovel/temFibra olhando o CNPJ inteiro (todos os pedidos não cancelados). */
export async function getFatos(cnpj: string): Promise<FatosCnpj> {
  const { data } = await supabase
    .from('v_planilha_mae')
    .select('familia, quadro_atual')
    .eq('cnpj', cnpj)
    .neq('quadro_atual', 'PEDIDO_CANCELADO');
  let temMovel = false;
  let temFibra = false;
  for (const it of (data as any[]) || []) {
    const fam = String(it?.familia || '');
    if (FAMILIAS_MOVEL.has(fam)) temMovel = true;
    if (FAMILIAS_FIXA.has(fam)) temFibra = true;
  }
  return { temMovel, temFibra };
}

/** Avalia o pedido no fechamento. Retorna null se não der p/ avaliar (fail-open). */
export async function avaliarConvergenciaPedido(pedido: any): Promise<AvaliacaoPedido | null> {
  const cnpj = await getCnpj(pedido);
  if (!cnpj) return null;
  const [fatos, cobertura] = await Promise.all([getFatos(cnpj), getCoberturaCnpj(cnpj)]);
  const gate = avaliarConvergenciaGate(fatos, cobertura);
  return { cnpj, fatos, cobertura, gate };
}

async function idsFamiliaPedido(pedidoId: string): Promise<string[]> {
  const { data: pedido, error } = await (supabase as any)
    .from('pedidos').select('id, pedido_mae_id').eq('id', pedidoId).maybeSingle();
  if (error || !pedido) throw new Error(`Pedido não encontrado: ${error?.message || pedidoId}`);
  const raizId = pedido.pedido_mae_id || pedido.id;
  const { data: familia, error: familiaError } = await (supabase as any)
    .from('pedidos').select('id').or(`id.eq.${raizId},pedido_mae_id.eq.${raizId}`);
  if (familiaError) throw new Error(`Falha ao localizar a família do pedido: ${familiaError.message}`);
  return (familia || []).map((p: any) => String(p.id));
}

export async function gravarConvergencia(pedidoId: string, payload: ConvergenciaPayload): Promise<void> {
  // Coluna pedidos.convergencia ainda não consta no types.ts gerado → cast localizado.
  const ids = await idsFamiliaPedido(pedidoId);
  const { data, error } = await (supabase as any)
    .from('pedidos').update({ convergencia: payload }).in('id', ids).select('id');
  if (error) throw new Error(`Falha ao gravar a convergência: ${error.message}`);
  if (!data || data.length !== ids.length) throw new Error('O banco não confirmou a convergência em toda a família do pedido.');
}

/** Registra um evento na timeline do CNPJ (tabela movimento). */
export async function registrarTimelineConvergencia(args: {
  pedidoId: string;
  cnpj: string;
  descricao: string;
  usuarioId: string | null;
  payload?: any;
  destaque?: boolean;
}): Promise<void> {
  const { error } = await (supabase as any).from('movimento').insert({
    entidade: 'pedido',
    entidade_id: args.pedidoId,
    cnpj: args.cnpj,
    tipo: 'nota',
    descricao: args.descricao,
    usuario_id: args.usuarioId,
    payload: args.payload ?? null,
    destaque: args.destaque ?? false,
  });
  if (error) console.warn('[convergencia] falha ao gravar timeline', error.message);
}

/** Status quando o gate decide "passa" automaticamente (sem modal). */
export function statusAutoPassa(c: ClassificacaoConvergencia): StatusConvergencia | null {
  return c === 'CONVERGENTE' ? 'aprovado com fibra' : null;
}

// ── Decisão da gerente (Fase 5) ───────────────────────────────────────────────
const STATUS_PASSA: StatusConvergencia[] = ['aprovado com fibra', 'aprovado sem fibra', 'solicitação de carteira'];

/** Convergência já gravada no pedido (mãe). */
export async function getConvergenciaPedido(pedidoId: string): Promise<ConvergenciaPayload | null> {
  const { data } = await (supabase as any).from('pedidos').select('convergencia').eq('id', pedidoId).maybeSingle();
  return (data?.convergencia as ConvergenciaPayload) ?? null;
}

/** O status já está resolvido como "passa" (consultor ou gerente aprovou)? */
export function statusJaResolvidoPassa(status?: string | null): boolean {
  return !!status && STATUS_PASSA.includes(status as StatusConvergencia);
}


/** Grava a decisão em pedidos.convergencia e ESTOURA se nada foi salvo — antes o
 *  erro era mudo: o toast dizia "aprovado/encaminhado", nada mudava e o card voltava. */
async function salvarDecisaoConvergencia(pedidoId: string, novo: any): Promise<void> {
  const ids = await idsFamiliaPedido(pedidoId);
  const { data, error } = await (supabase as any)
    .from('pedidos').update({ convergencia: novo }).in('id', ids).select('id');
  if (error) throw new Error(`Não salvou a decisão: ${error.message}`);
  if (!data || data.length !== ids.length) throw new Error('A decisão não foi salva em toda a família do pedido. Avise o suporte.');
}

// ── Solicitação de carteira (Fase 6 — supervisora de qualidade) ───────────────
export interface SolicitacaoCarteira {
  pedidoId: string;
  numeroPedido: string | null;
  cnpj: string | null;
  razaoSocial: string | null;
  territorioAace: string | null;
  qtdLinhasMovel: number | null;
  decididoEm: string | null;
  convergencia: ConvergenciaPayload | null;
}

/**
 * Lista as solicitações de carteira pendentes (conv_status='solicitação de carteira'),
 * exceto as já tratadas (convergencia.carteira_tratada=true). Vem da fibra vendida p/
 * CNPJ cuja base MÓVEL está em outro parceiro.
 */
export async function listarSolicitacoesCarteira(): Promise<SolicitacaoCarteira[]> {
  const { data } = await (supabase as any)
    .from('v_planilha_mae')
    .select('pedido_id, numero_pedido, cnpj, razao_social, convergencia, conv_status, conv_territorio_aace')
    .eq('conv_status', 'solicitação de carteira');
  const porPedido = new Map<string, SolicitacaoCarteira>();
  for (const r of (data as any[]) || []) {
    const conv: any = r.convergencia || {};
    if (conv.carteira_tratada === true) continue; // já tratada → sai da lista
    if (!porPedido.has(r.pedido_id)) {
      porPedido.set(r.pedido_id, {
        pedidoId: r.pedido_id,
        numeroPedido: r.numero_pedido != null ? String(r.numero_pedido) : null,
        cnpj: r.cnpj ?? null,
        razaoSocial: r.razao_social ?? null,
        territorioAace: r.conv_territorio_aace ?? conv.territorio_aace ?? null,
        qtdLinhasMovel: conv.qtd_linhas_movel ?? null,
        decididoEm: conv.decidido_em ?? null,
        convergencia: r.convergencia ?? null,
      });
    }
  }
  return Array.from(porPedido.values());
}

/** Supervisora marca a solicitação de carteira como TRATADA (mantém o status p/ auditoria). */
export async function marcarCarteiraTratada(args: {
  pedidoId: string; cnpj: string; observacao: string; usuarioId: string | null;
}): Promise<void> {
  const atual = (await getConvergenciaPedido(args.pedidoId)) || ({} as ConvergenciaPayload);
  const novo: any = {
    ...atual,
    carteira_tratada: true,
    carteira_tratada_por: args.usuarioId,
    carteira_tratada_em: new Date().toISOString(),
    carteira_observacao: args.observacao || null,
  };
  await salvarDecisaoConvergencia(args.pedidoId, novo);
  await registrarTimelineConvergencia({
    pedidoId: args.pedidoId, cnpj: args.cnpj,
    descricao: `Convergência: solicitação de carteira TRATADA pela supervisora${args.observacao ? ` — ${args.observacao}` : ''}`,
    usuarioId: args.usuarioId, payload: novo, destaque: false,
  });
}
